import { useMemo, useState } from 'react';
import { Box } from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowSelectionModel,
  GridSortModel
} from '@mui/x-data-grid';

import DataGridLoading from '../../shared/datagrid/DataGridLoading';
import DataGridNoRows from '../../shared/datagrid/DataGridNoRows';
import { EsgRecord, EsgRecordUpdateResponse } from '../../../types/syntiro_honey';
import {
  EntityCell,
  MetricCell,
  RawLabelCell,
  StatusCell,
  TrustCell,
  ValueCell
} from './RecordGridCells';

interface RecordsGridProps {
  /** The records on the current page. */
  records: Array<EsgRecord>;
  /** Total number of records matching the filters (server-side). */
  total: number;
  /** Whether the list request is in flight. */
  loading: boolean;
  /** Current server-side pagination model. */
  paginationModel: GridPaginationModel;
  /** Pagination change handler. */
  onPaginationModelChange: (model: GridPaginationModel) => void;
  /** Current server-side sort model (whitelisted fields only). */
  sortModel: GridSortModel;
  /** Sort change handler. */
  onSortModelChange: (model: GridSortModel) => void;
  /** Currently selected row ids (for bulk actions). */
  selection: GridRowSelectionModel;
  /** Selection change handler. */
  onSelectionChange: (selection: GridRowSelectionModel) => void;
  /** Called with the clicked record to open the detail drawer. */
  onRowOpen: (record: EsgRecord) => void;
  /** Called after an inline edit (e.g. assigning a suggested metric) to refresh the list. */
  onRecordMutated: () => void;
  /**
   * Called with the updated record after the inline confirm button promotes a
   * suggested metric, so the parent can offer to apply it to the rows sharing its
   * label. Separate from {@link onRecordMutated}, which fires for every mutation.
   */
  onMappingConfirmed?: (updated: EsgRecordUpdateResponse) => void;
  /**
   * The file the review is scoped to, if any. A record whose own `fileId` differs
   * came from an earlier upload in this workbook's lineage (carried over unchanged),
   * so the grid marks it — otherwise a re-upload's locked rows look unexplained.
   */
  viewedFileId?: string;
  /** Viewer mode: no selection checkboxes, no confirm/assign affordances. */
  readOnly?: boolean;
}

/**
 * Server-driven records grid: pagination, sorting (createdAt /
 * overallConfidence / rawLabel only — the backend whitelist) and checkbox
 * selection for bulk actions. Clicking a row opens the detail drawer.
 * @returns {JSX.Element}
 */
export default function RecordsGrid(props: RecordsGridProps) {
  // Which unmapped row has its inline suggestion picker open. Local UX state — the
  // free DataGrid has no native row-expansion, so we drive it via dynamic row height.
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { onRowOpen, onRecordMutated, onMappingConfirmed, viewedFileId, readOnly = false } = props;

  const columns = useMemo<Array<GridColDef<EsgRecord>>>(
    () => [
      // Narrow, with a tooltip carrying the full text: at flex 1.4 this column grew
      // to ~360px to render the word "Electricity" while Trust clipped its confirm
      // action. It still leads the row — it is what the metric beside it is matched
      // against — but it no longer takes width from the columns that need it.
      {
        field: 'rawLabel',
        headerName: 'Raw label',
        flex: 0.7,
        minWidth: 150,
        renderCell: (params: GridRenderCellParams<EsgRecord>) => (
          <RawLabelCell record={params.row} />
        )
      },
      {
        field: 'metricId',
        headerName: 'Metric',
        sortable: false,
        flex: 1,
        minWidth: 200,
        renderCell: (params: GridRenderCellParams<EsgRecord>) => (
          <MetricCell
            record={params.row}
            expanded={!readOnly && expandedId === params.row.id}
            onToggleExpand={
              readOnly
                ? undefined
                : () =>
                    setExpandedId((current) => (current === params.row.id ? null : params.row.id))
            }
            onAssigned={() => {
              setExpandedId(null);
              onRecordMutated();
            }}
            onOpenDetail={() => {
              setExpandedId(null);
              onRowOpen(params.row);
            }}
          />
        )
      },
      {
        field: 'value',
        headerName: 'Value',
        sortable: false,
        flex: 1,
        minWidth: 150,
        renderCell: (params: GridRenderCellParams<EsgRecord>) => <ValueCell record={params.row} />
      },
      {
        field: 'entity',
        headerName: 'Entity',
        sortable: false,
        flex: 1,
        minWidth: 140,
        renderCell: (params: GridRenderCellParams<EsgRecord>) => <EntityCell record={params.row} />
      },
      {
        field: 'periodLabel',
        headerName: 'Period',
        sortable: false,
        minWidth: 110,
        valueGetter: (params) => params.row.periodLabel ?? '—'
      },
      // Site + Assignee columns are deliberately absent: entity resolution and
      // assignment are later slices, so today they would be all-empty columns.
      // Both values remain visible/editable in the detail drawer.
      {
        // Sortable on `overallConfidence`: it is on the backend sort whitelist, so
        // the header click drives the existing server-side sort.
        field: 'overallConfidence',
        headerName: 'Trust',
        // Holds mapping chip + score + confirm button on ONE line (~272px). At 190
        // the confirm action was clipped mid-word — the defect this width fixes.
        minWidth: 270,
        renderCell: (params: GridRenderCellParams<EsgRecord>) => (
          <TrustCell
            record={params.row}
            readOnly={readOnly}
            onConfirmed={(updated) => {
              onRecordMutated();
              onMappingConfirmed?.(updated);
            }}
          />
        )
      },
      {
        field: 'status',
        headerName: 'Status',
        sortable: false,
        // Icons, not chips: status + markers + the rare carried-over indicator, each
        // with a tooltip and an accessible name. The ~80px this returned went to
        // Trust. Deliberately not flexible — it holds a fixed number of glyphs.
        minWidth: 90,
        renderCell: (params: GridRenderCellParams<EsgRecord>) => (
          <StatusCell record={params.row} viewedFileId={viewedFileId} />
        )
      }
      // `Created` is deliberately absent: it is never a review criterion, and its
      // fixed width was being taken from the two columns that are. It remains on the
      // record's detail modal and in its history.
    ],
    [expandedId, onRowOpen, onRecordMutated, onMappingConfirmed, viewedFileId, readOnly]
  );

  return (
    // minWidth: 0 lets the grid shrink to its flex parent instead of overflowing the
    // viewport, so the DataGrid's own horizontal scroller engages (Maxine #7).
    //
    // The column minimums total 1160px, down from 1540 originally and 1250 after the
    // first pass. That fits BOTH 1280px (1184 usable after the view's px:6) and
    // 1440px, so the grid no longer scrolls horizontally at any laptop width — the
    // truncation that pushed Raw label and Metric into ellipses is gone rather than
    // merely mitigated. Re-check this budget before widening any column or adding one.
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <DataGrid
        autoHeight
        // A page is ≤100 rows × 11 columns — virtualization buys nothing at that
        // size, and disabling it lets tests (jsdom has no viewport) render every
        // cell, including the per-row confirm affordance (same call as AdminPanel).
        disableVirtualization
        // Only the expanded row grows (to fit its inline suggestion picker); every
        // other row keeps the default height.
        getRowHeight={(params) => (params.id === expandedId ? 'auto' : null)}
        rows={props.records}
        columns={columns}
        rowCount={props.total}
        loading={props.loading}
        paginationMode="server"
        paginationModel={props.paginationModel}
        onPaginationModelChange={props.onPaginationModelChange}
        pageSizeOptions={[10, 25, 50, 100]}
        sortingMode="server"
        sortModel={props.sortModel}
        onSortModelChange={props.onSortModelChange}
        checkboxSelection={!readOnly}
        keepNonExistentRowsSelected
        disableRowSelectionOnClick
        rowSelectionModel={props.selection}
        onRowSelectionModelChange={props.onSelectionChange}
        // Approved records are done + immutable: they stay visible (Maxine) but
        // never invite a bulk action that would be a no-op/409, so they are not
        // selectable — the checkbox is disabled while the row still opens.
        isRowSelectable={(params) => (params.row as EsgRecord).status !== 'APPROVED'}
        onRowClick={(params) => props.onRowOpen(params.row as EsgRecord)}
        columnHeaderHeight={props.records.length < 1 ? 0 : 56}
        // Tag approved rows so they read as settled (muted) rather than actionable.
        getRowClassName={(params) =>
          (params.row as EsgRecord).status === 'APPROVED' ? 'row-approved' : ''
        }
        slots={{
          noRowsOverlay: () => (
            <DataGridNoRows missing_label={{ one: 'record', many: 'records' }} />
          ),
          loadingOverlay: () => <DataGridLoading columns={columns.length} />
        }}
        sx={{
          // The app shell still carries Create React App's boilerplate
          // `.App { text-align: center }` (src/App.css), which INHERITS into every
          // block-level cell child — so two-line cells (entity name over its level,
          // value over its client original) rendered centred on a shared axis
          // instead of aligned to a left edge the eye can scan down. Fixing it at
          // the source would re-align every view in the platform; this scopes the
          // correction to the grid, and covers any cell added later.
          '& .MuiDataGrid-cell': { textAlign: 'left' },
          '& .MuiDataGrid-row': { cursor: 'pointer' },
          // Locked-row treatment: an approved row is muted and its (disabled)
          // checkbox is dimmed, so "done" is legible at a glance without hiding it.
          '& .MuiDataGrid-row.row-approved': {
            backgroundColor: (theme) => theme.palette.action.hover
          },
          '& .MuiDataGrid-row.row-approved .MuiDataGrid-cellCheckbox': { opacity: 0.35 }
        }}
      />
    </Box>
  );
}
